import { useCallback, useEffect, useState } from "react";

type DotPosition = {
  x: number;
  y: number;
  month: string;
};

type BaseDatasetItem = {
  month: string;
  rawMonth: string;
  vendorAcute: number;
  vendorRegular: number;
  vendorStateRegulated: number;
  eManifestAcute: number;
  eManifestRegular: number;
  eManifestStateRegulated: number;
  isOverReportedThreshold: boolean;
};

type ChartControlsState = {
  compareEPAWithVendor: boolean;
  showHazardousWaste: boolean;
  showAcutelyHazardousWaste: boolean;
  showStateRegulatedWaste: boolean;
};

interface UseChartDotPositionsProps {
  baseDataset: BaseDatasetItem[];
  chartControls: ChartControlsState;
  chartContainerRef: React.RefObject<HTMLDivElement | null>;
}

export function useChartDotPositions({
  baseDataset,
  chartControls,
  chartContainerRef,
}: UseChartDotPositionsProps) {
  const [dotPositions, setDotPositions] = useState<DotPosition[]>([]);

  // Function to update dot positions based on current bar positions
  const updateDotPositions = useCallback(() => {
    if (!chartContainerRef.current || !baseDataset) return;

    // Add small delay to fix bug where window resize and mouse over uses old dot positions
    setTimeout(() => {
      if (!chartContainerRef.current || !baseDataset) return;

      const chartContainer = chartContainerRef.current;
      const minDotY = 10; // Minimum distance from top of chart area to place dots

      // Find all bars in the chart
      const allBars = Array.from(chartContainer.querySelectorAll("rect.MuiBarElement-root"));

      // Prefetch all bar attributes in one go to minimize DOM calls
      type BarData = {
        element: Element;
        x: number;
        y: number;
        width: number;
      };

      const barDataList: BarData[] = allBars.map((bar) => {
        // Get all attributes in one go to minimize DOM queries
        const x = parseFloat(bar.getAttribute("x") || "0") ?? 0;
        const y = parseFloat(bar.getAttribute("y") || "0") ?? 0;
        const width = parseFloat(bar.getAttribute("width") || "0") ?? 0;

        return { element: bar, x, y, width };
      });

      // Filter out bars with duplicate X positions (keep the tallest bar per X position)
      const barsByX = new Map<number, BarData>();

      barDataList.forEach((bar) => {
        const existingBar = barsByX.get(bar.x);
        if (!existingBar) {
          // First bar at this X position
          barsByX.set(bar.x, bar);
        } else {
          // Compare with existing bar and keep the tallest one (smallest Y value) for dot placement
          if (bar.y < existingBar.y) {
            barsByX.set(bar.x, bar);
          }
        }
      });

      // Convert map values to array
      const uniqueBars = Array.from(barsByX.values());

      // Calculate dynamic threshold based on expected spacing
      const chartWidth = chartContainer.clientWidth;
      const expectedMonthWidth = chartWidth / baseDataset.length;
      const groupingThreshold = expectedMonthWidth * 0.4; // Bars within 40% of month width belong together

      // Sort bars by X position first, then group in one pass
      // This works for both single-bar and multi-bar charts:
      // - Single-bar: Each bar is ~1 month width apart, so each forms its own group
      // - Multi-bar: Bars within same month are close, so they group together
      const sortedGroups = uniqueBars
        .sort((a, b) => a.x - b.x)
        .reduce(
          (groups, bar) => {
            const lastGroup = groups[groups.length - 1];

            if (lastGroup && Math.abs(bar.x - lastGroup[0]) < groupingThreshold) {
              lastGroup[1].push(bar);
            } else {
              groups.push([bar.x, [bar]]);
            }

            return groups;
          },
          [] as Array<[number, BarData[]]>
        );

      const newPositions: DotPosition[] = [];

      // Unexpected, but if this happens, we should fail early and not try to position dots
      if (sortedGroups.length !== baseDataset.length && sortedGroups.length > 0) {
        setDotPositions([]);
        return;
      }

      // Process each month
      baseDataset.forEach((monthData, monthIndex) => {
        // Skip if not over threshold
        if (!monthData.isOverReportedThreshold) return;

        // Check if this month has any visible data
        const hasVisibleHazardousWasteData =
          chartControls.showHazardousWaste &&
          (monthData.vendorRegular > 0 ||
            (chartControls.compareEPAWithVendor && monthData.eManifestRegular > 0));

        const hasVisibleAcutelyHazardousWasteData =
          chartControls.showAcutelyHazardousWaste &&
          (monthData.vendorAcute > 0 ||
            (chartControls.compareEPAWithVendor && monthData.eManifestAcute > 0));

        const hasVisibleStateRegulatedWasteData =
          chartControls.showStateRegulatedWaste &&
          (monthData.vendorStateRegulated > 0 ||
            (chartControls.compareEPAWithVendor && monthData.eManifestStateRegulated > 0));

        const hasVisibleData =
          hasVisibleHazardousWasteData ||
          hasVisibleAcutelyHazardousWasteData ||
          hasVisibleStateRegulatedWasteData;

        // Skip if no visible data
        if (!hasVisibleData) return;

        // Safely get the bar group for this month - skip if no bars exist for this month
        if (monthIndex >= sortedGroups.length) return;
        const monthBarGroup = sortedGroups[monthIndex];

        const monthBars = monthBarGroup[1]; // index 0 is the X position, index 1 is the bars
        if (!monthBars || monthBars.length === 0) return;

        // Calculate dot position based on bars in this month
        let dotX = 0;
        let dotY = minDotY;

        if (monthBars.length >= 2) {
          // Multiple bars (e.g., vendor and emanifest) - place dot between them
          const bar1 = monthBars[0];
          const bar2 = monthBars[1];

          // Calculate centers of each bar
          const bar1Center = bar1.x + bar1.width / 2;
          const bar2Center = bar2.x + bar2.width / 2;

          // Dot X is midpoint between the two bar centers
          dotX = (bar1Center + bar2Center) / 2;

          // Find the tallest bar (smallest Y value)
          let tallestBarY = Infinity;
          monthBars.forEach((bar) => {
            if (bar.y < tallestBarY) {
              tallestBarY = bar.y;
            }
          });

          // Position dot 10px above the tallest bar
          if (tallestBarY !== Infinity) {
            dotY = Math.max(tallestBarY - 10, minDotY);
          }
        } else if (monthBars.length === 1) {
          // Single bar - center on that bar
          const bar = monthBars[0];
          dotX = bar.x + bar.width / 2;
          dotY = Math.max(bar.y - 10, minDotY);
        }

        newPositions.push({
          x: dotX,
          y: dotY,
          month: monthData.month,
        });
      });

      setDotPositions(newPositions);
    }, 100); // Wait 100ms for DOM to stabilize
  }, [baseDataset, chartControls, chartContainerRef]);

  // Update dot positions when data changes or chart renders
  useEffect(() => {
    const timer = setTimeout(updateDotPositions, 300);
    return () => clearTimeout(timer);
  }, [
    baseDataset,
    updateDotPositions,
    chartControls.showHazardousWaste,
    chartControls.showAcutelyHazardousWaste,
    chartControls.compareEPAWithVendor,
  ]);

  // Add resize observer to update dot positions when chart resizes
  useEffect(() => {
    if (!chartContainerRef.current) return;

    let timeoutId: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver(() => {
      // Debounce the update to avoid excessive recalculations
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateDotPositions();
      }, 300);
    });

    resizeObserver.observe(chartContainerRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [updateDotPositions, chartContainerRef]);

  return {
    dotPositions,
    updateDotPositions,
  };
}
